import {Component, OnDestroy} from '@angular/core';
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {Clause} from '../../../../models/product.model';
import {Consent} from '../../../../models/simplified-ocean-policy.model';

@Component({
    template: ''
})
export abstract class ValidatableFormComponent implements OnDestroy {
    public abstract formGroup: FormGroup;
    protected abstract errors = {};
    private formSubscriptions: Subscription = new Subscription();
    formErrors$: Observable<any>;

    constructor(formErrors$: Observable<any>) {
        this.formErrors$ = formErrors$;
        this.formSubscriptions.add(
            this.formErrors$.subscribe((errors) => {
                this.errors = errors;
                this.scrollToFirstError();
            })
        );
    }

    ngOnDestroy(): void {
        this.formSubscriptions.unsubscribe();
    }

    protected setStateValidator(controlName) {
        return () => this.formErrors$.pipe(
            map((errors) => {
                const result = errors[controlName]
                    ? errors[controlName].reduce((res, item) => {
                        res[item] = true;
                        return res;
                    }, {})
                    : null;
                if (this.formGroup && controlName) {
                    const parsedControlName = controlName.replace('[', '.').replace(']', '');
                    const control = this.formGroup.get(parsedControlName);
                    if (control) {
                        control.setErrors(result);
                    }
                }
                return result;
            }),
        );
    }

    createFormControl(value: any, stateCode: string, disabled?: boolean, required?: boolean, maxLength?: number) {
        const validators = [];
        const asyncValidators = [];
        if (required) {
            validators.push(Validators.required);
        }
        if (maxLength > 0) {
            validators.push(Validators.maxLength(maxLength));
        }
        if (stateCode) {
            asyncValidators.push(this.setStateValidator(stateCode));
        }
        return new FormControl({value, disabled}, validators, asyncValidators);
    }

    createAddressFormGroup(prefix = 'address.') {
        return new FormGroup({
            apartmentNumber: this.createFormControl('', `${prefix}apartmentNumber`),
            city: this.createFormControl('', `${prefix}city`),
            country: this.createFormControl('', `${prefix}country`),
            streetNumber: this.createFormControl('', `${prefix}streetNumber`),
            postOffice: this.createFormControl('', `${prefix}postOffice`),
            street: this.createFormControl('', `${prefix}street`),
            streetPrefix: this.createFormControl('', `${prefix}streetPrefix`),
            voivodeship: this.createFormControl('', `${prefix}voivodeship`),
            postalCode: this.createFormControl('', `${prefix}postalCode`),
        });
    }

    createPartyCpiFormGroup(prefix: string) {
        return new FormGroup({
            firstName: this.createFormControl('', `${prefix}firstName`),
            lastName: this.createFormControl('', `${prefix}lastName`),
            pesel: this.createFormControl('', `${prefix}pesel`),
            gender: this.createFormControl(undefined, `${prefix}gender`),
            citizenship: this.createFormControl(undefined, `${prefix}citizenship`),
            email: this.createFormControl('', `${prefix}email`),
            phoneNumber: this.createFormControl('', `${prefix}phoneNumber`),
            birthDate: this.createFormControl('', `${prefix}birthDate`),
            documentType: this.createFormControl('', `${prefix}documentType`),
            documentNumber: this.createFormControl('', `${prefix}documentNumber`),
            address: this.createAddressFormGroup(`${prefix}address.`),
        });
    }

    createClausesFormArray(clauseList: Clause[], prefix = 'clauseList') {
        if (!clauseList) {
            return new FormArray([]);
        }
        const formArray = [];
        clauseList.forEach((clause, index) => {
            const group = new FormGroup({
                item: new FormGroup({
                    code: new FormControl(clause?.item?.code),
                    description: new FormControl({value: clause?.item?.description, disabled: true}),
                }),
                selected: this.createFormControl(clause.selected, `${prefix}[${index}].selected`),
            });
            group.patchValue(clause);
            formArray.push(group);
        });
        return new FormArray(formArray);
    }

    createConsentsFormArray(consentsList: Consent[], prefix = 'consents') {
        if (!consentsList) {
            return new FormArray([]);
        }
        const formArray = [];
        consentsList.forEach((consent, index) => {
            const group = new FormGroup({
                code: new FormControl(consent.code),
                description: new FormControl({value: consent.description, disabled: true}),
                checked: this.createFormControl(consent.checked, `${prefix}[${index}].checked`),
            });
            group.patchValue(consent);
            formArray.push(group);
        });
        return new FormArray(formArray);
    }

    createInsurerRepositoryFormGroup(prefix: string) {
        return new FormGroup({
            personType: this.createFormControl('', `${prefix}personType`),
            firstName: this.createFormControl('', `${prefix}firstName`),
            lastName: this.createFormControl('', `${prefix}lastName`),
            email: this.createFormControl('', `${prefix}email`),
            phone: this.createFormControl('', `${prefix}phone`),

            city: this.createFormControl('', `${prefix}city`, true),
            postalCode: this.createFormControl('', `${prefix}postalCode`, true),
            street: this.createFormControl('', `${prefix}street`, true),
            streetNumber: this.createFormControl('', `${prefix}streetNumber`, true),

            companyName: this.createFormControl('', `${prefix}companyName`),
            taxId: this.createFormControl('', `${prefix}taxId`),
            regon: this.createFormControl('', `${prefix}regon`),
        });
    }

    setEnabledOnControl(controlName, enabled, formGroup: FormGroup | FormArray = this.formGroup, emitEvent = true) {
        const control = formGroup.get(controlName);
        if (!control) {
            return;
        }
        // handle children controls
        if (control instanceof FormGroup || control instanceof FormArray) {
            const group = control instanceof FormGroup ? (control as FormGroup) : (control as FormArray);
            Object.keys(group.controls).forEach(field => {
                this.setEnabledOnControl(field, enabled, group);
            });
            return;
        }
        // otherwise handle value
        if (enabled) {
            control.enable({emitEvent});
        } else {
            control.disable({emitEvent});
        }
    }

    get errorsFieldNull(): string[] {
        const errorKey = Object.keys(this.errors).find(key => key === 'null');
        return errorKey ? this.errors[errorKey] : [];
    }

    scrollToFirstError() {
        // form-error-scroll-hook
        setTimeout(() => {
            const classElement = document.getElementsByClassName('form-error-scroll-hook');
            if (classElement.length > 0) {
                classElement[0].scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'center'
                });
            }
        }, 100);
    }
}
